import { useState } from 'react'
import { useCurrentUnit } from '../hooks/useCurrentUnit.js'
import { useRollupBrief } from '../hooks/useRollupBrief.js'
import { useGroupKpi } from '../hooks/useGroupKpi.js'
import { AGENT_LABELS } from '../lib/agents.js'
import KitchenLoader from './KitchenLoader.jsx'
import AgentRanBadge from './AgentRanBadge.jsx'
import GroupKpiBar from './group/GroupKpiBar.jsx'
import SalesBreakdownTable from './group/SalesBreakdownTable.jsx'
import LaborBreakdownTable from './group/LaborBreakdownTable.jsx'
import WasteBreakdownTable from './group/WasteBreakdownTable.jsx'
import ReorderBreakdownTable from './group/ReorderBreakdownTable.jsx'
import GroupCateringPipeline from './group/GroupCateringPipeline.jsx'
import GroupMenuEngineering from './group/GroupMenuEngineering.jsx'
import CombinedReorderPipeline from './group/CombinedReorderPipeline.jsx'
import GroupComparison from './group/GroupComparison.jsx'
import AiReceiptsCard from './AiReceiptsCard.jsx'

// GroupDashboardScreen — the All-Units roll-up surface (Ship 2).
//
// Renders the AI-composed group roll-up Huddle: each unit's individual
// morning huddle synthesized into one master brief whose headline is a
// SPECIFIC cross-unit insight. App mounts this as a state overlay when
// UnitContext mode === 'all_units' (no router). Picking a single unit
// in the switcher returns the user to normal tab content.
//
// Visual contract mirrors the single-unit HeroBriefCard: charcoal
// #2C2C2A wrapper, sentence-case eyebrow, white headline, muted
// narrative; KitchenLoader (tone=dark) for the generation state, never
// a blue spinner; calm fallback on error; NO AI-cost / model mentions.

const LOADER_MESSAGES = [
  'Reading each location…',
  'Comparing units side by side…',
  'Finding the cross-unit play…',
  'Plating your group roll-up…',
]

const INSIGHT_TYPE_LABEL = {
  staff_sharing:           'staff sharing',
  supplier_consolidation:  'supplier consolidation',
  inventory_transfer:      'inventory transfer',
  performance_comparison:  'performance comparison',
}

// Sentence-case "9:42 am" — matches HeroBriefCard's formatter.
function formatGeneratedAt(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const s = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return `Generated ${s.replace(/\bAM\b/, 'am').replace(/\bPM\b/, 'pm')}`
}

// Tile id → drill-down view, and back the other way for rendering.
// NOTE: 'catering-breakdown' is intentionally absent here — it routes to
// the full GroupCateringPipeline below, BEFORE this lookup. The old
// CateringBreakdownTable placeholder is no longer reachable.
const BREAKDOWN_TABLES = {
  'sales-breakdown':    SalesBreakdownTable,
  'labor-breakdown':    LaborBreakdownTable,
  'waste-breakdown':    WasteBreakdownTable,
  'reorder-breakdown':  ReorderBreakdownTable,
}

export default function GroupDashboardScreen() {
  // --- All hooks BEFORE any conditional return (React #310). ---
  const { allUnits, currentGroupId } = useCurrentUnit()
  const { phase, brief, generatedAt, regenerate } = useRollupBrief(currentGroupId)
  const kpi = useGroupKpi(currentGroupId)
  const [view, setView] = useState('dashboard')

  const unitSummaries  = Array.isArray(brief?.unit_summaries) ? brief.unit_summaries : []
  const crossInsights  = Array.isArray(brief?.cross_unit_insights) ? brief.cross_unit_insights : []

  // Catering tile drills into the full combined pipeline (Ship 4), not a
  // breakdown table. Checked BEFORE the BREAKDOWN_TABLES lookup. All hooks
  // are already declared above this early return (React #310).
  if (view === 'catering-breakdown') {
    return <GroupCateringPipeline groupId={currentGroupId} onBack={() => setView('dashboard')} />
  }

  // Cross-unit menu engineering overlay (Ship 5). Beside the catering
  // early return; all hooks are already declared above (React #310). The
  // overlay owns its own data hooks, so the screen needs no new hooks.
  if (view === 'menu-engineering') {
    return <GroupMenuEngineering groupId={currentGroupId} onBack={() => setView('dashboard')} />
  }

  // Cross-unit consolidated reorder overlay (Ship 6). The "Items Below Par"
  // KPI tile now drills here (GroupKpiBar VIEW.belowPar = 'reorder'); the
  // old ReorderBreakdownTable stays in BREAKDOWN_TABLES as an unlinked
  // fallback. All hooks are already declared above (React #310).
  if (view === 'reorder') {
    return <CombinedReorderPipeline groupId={currentGroupId} onBack={() => setView('dashboard')} />
  }

  // Cross-unit comparison overlay (Ship 7, FINAL). Beside the other overlay
  // early returns; all hooks are already declared above (React #310). The
  // overlay owns its own data hooks, so the screen needs no new hooks.
  if (view === 'comparison') {
    return <GroupComparison groupId={currentGroupId} onBack={() => setView('dashboard')} />
  }

  const BreakdownTable = BREAKDOWN_TABLES[view] || null
  if (BreakdownTable) {
    return (
      <div className="mx-auto max-w-screen-sm px-4 py-6">
        <BreakdownTable perUnit={kpi.perUnit} onBack={() => setView('dashboard')} />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-screen-sm px-4 py-6">
      <GroupKpiBar
        phase={kpi.phase}
        data={kpi.data}
        onTileClick={setView}
        onRefresh={kpi.regenerate}
      />

      {/* Cross-unit menu engineering entry point (Ship 5). A labeled
          secondary button, NOT a KPI tile. Only present on the group
          all-units surface — single-unit users never mount this screen. */}
      <div className="mb-4 mt-1 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => setView('comparison')}
          className="inline-flex items-center gap-1.5 rounded-lg border border-black/10 bg-white px-3 py-1.5 text-sm font-medium text-black/70 shadow-sm hover:bg-black/[0.03] hover:text-black/85"
        >
          Compare units
          <span aria-hidden="true">→</span>
        </button>
        <button
          type="button"
          onClick={() => setView('menu-engineering')}
          className="inline-flex items-center gap-1.5 rounded-lg border border-black/10 bg-white px-3 py-1.5 text-sm font-medium text-black/70 shadow-sm hover:bg-black/[0.03] hover:text-black/85"
        >
          Menu engineering
          <span aria-hidden="true">→</span>
        </button>
      </div>

      <section
        className="relative rounded-2xl bg-charcoal-hero p-5 shadow-sm"
        aria-label={AGENT_LABELS.groupRollup}
      >
        {/* Eyebrow + refresh affordance (ready only). */}
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-[11px] font-medium text-charcoal-text-secondary">
            {AGENT_LABELS.groupRollup}
            {allUnits.length > 0 && ` · ${allUnits.length} locations`}
          </div>
          <div className="flex items-baseline gap-2">
            {phase === 'ready' && (
              <AgentRanBadge generatedAt={generatedAt} tone="dark" />
            )}
            {phase === 'ready' && (
              <button
                type="button"
                onClick={() => regenerate()}
                className="text-[11px] text-charcoal-text-secondary hover:text-white"
                aria-label="Refresh roll-up"
              >
                Refresh
              </button>
            )}
          </div>
        </div>

        {/* Phase: generating — KitchenLoader, dark tone (never blue spinner). */}
        {phase === 'generating' && (
          <div className="mt-3">
            <KitchenLoader align="row" size="sm" tone="dark" messages={LOADER_MESSAGES} />
          </div>
        )}

        {/* Phase: idle — calm prompt in the charcoal palette. */}
        {phase === 'idle' && (
          <div className="mt-3">
            <p className="text-[12px] leading-relaxed text-charcoal-text-tertiary">
              Tap to compose your group roll-up.
            </p>
            <button
              type="button"
              onClick={() => regenerate()}
              className="mt-2 text-[12px] text-white/90 underline-offset-2 hover:underline"
            >
              Generate
            </button>
          </div>
        )}

        {/* Phase: ready — overview, top action, cross-unit insights
            (the whole point, shown prominently), then unit summaries. */}
        {phase === 'ready' && brief && (
          <>
            {brief.overview && (
              <div className="mt-3 text-[16px] sm:text-[18px] font-medium leading-snug text-white">
                {brief.overview}
              </div>
            )}

            {brief.top_action && (
              <div className="mt-3 rounded-md bg-white/5 px-3 py-2 text-[12px] text-charcoal-text-tertiary">
                <span className="text-[11px] font-medium text-charcoal-text-secondary">
                  Top action
                </span>
                <span className="ml-2 text-white/90">{brief.top_action}</span>
              </div>
            )}

            {crossInsights.length > 0 && (
              <div className="mt-4">
                <div className="text-[11px] font-medium text-charcoal-text-secondary">
                  Across your units
                </div>
                <ul className="mt-2 space-y-2">
                  {crossInsights.map((ins, i) => (
                    <li
                      key={i}
                      className="rounded-md border border-white/10 bg-white/[0.04] px-3 py-2.5"
                    >
                      <p className="text-[13px] leading-relaxed text-white/90">
                        {ins.insight}
                      </p>
                      {INSIGHT_TYPE_LABEL[ins.type] && (
                        <span className="mt-1.5 inline-block text-[10px] uppercase tracking-wide text-charcoal-text-secondary">
                          {INSIGHT_TYPE_LABEL[ins.type]}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {unitSummaries.length > 0 && (
              <div className="mt-4">
                <div className="text-[11px] font-medium text-charcoal-text-secondary">
                  By location
                </div>
                <ul className="mt-2 space-y-2.5">
                  {unitSummaries.map((u, i) => (
                    <li key={i} className="text-[12px] leading-relaxed">
                      <span className="font-medium text-white/90">{u.unit_name}</span>
                      <span className="ml-1.5 text-charcoal-text-tertiary">{u.summary}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {generatedAt && (
              <div className="mt-4 text-right text-[10px] text-charcoal-text-secondary">
                {formatGeneratedAt(generatedAt)}
              </div>
            )}
          </>
        )}

        {/* Phase: empty — deterministic nudge (tiny-group / timeout).
            retryable → show Refresh; otherwise no button. */}
        {phase === 'empty' && brief && (
          <>
            <div className="mt-3 text-[16px] sm:text-[18px] font-medium leading-snug text-white">
              {brief.overview || "We couldn't analyze enough across your units yet."}
            </div>
            {brief.narrative && (
              <p className="mt-2 text-[12px] leading-relaxed text-charcoal-text-tertiary">
                {brief.narrative}
              </p>
            )}
            {/* Deterministic next-action nudge — names exactly what fills
                the roll-up so a new group operator isn't left guessing.
                ONLY shown for a genuine no-data empty (retryable !== true).
                A timeout-empty (retryable === true) can occur on a fully
                populated group, so we suppress the "connect each location's
                POS" sentence there — the Refresh button below handles it. */}
            {brief.retryable !== true && (
              <p className="mt-2 text-[12px] leading-relaxed text-charcoal-text-tertiary">
                Connect each location's POS and generate that unit's brief — your
                group roll-up fills in once the units have data.
              </p>
            )}
            {brief.retryable === true && (
              <button
                type="button"
                onClick={() => regenerate()}
                className="mt-2 text-[12px] text-white/90 underline-offset-2 hover:underline"
              >
                Refresh
              </button>
            )}
          </>
        )}

        {/* Phase: error — calm deterministic line + retry. Never the raw
            error (a structured edge-fn message can be technical). */}
        {phase === 'error' && (
          <div className="mt-3">
            <p className="text-[12px] leading-relaxed text-charcoal-text-tertiary">
              Couldn't pull your group roll-up — tap to try again.
            </p>
            <button
              type="button"
              onClick={() => regenerate()}
              className="mt-2 text-[12px] text-white/90 underline-offset-2 hover:underline"
            >
              Retry
            </button>
          </div>
        )}
      </section>

      {/* AI Receipts — deterministic recap of what each agent did across
          the group this week. Rows click through to the matching overlay
          (catering / reorder / comparison) via setView. */}
      {currentGroupId && (
        <AiReceiptsCard
          scope={{ groupId: currentGroupId }}
          onTab={(tab) => setView(tab)}
          className="mt-4"
        />
      )}
    </div>
  )
}
